import axios from 'axios'
import { useCallback, useState, useMemo, useEffect } from 'react'

import { useAnalyticsEvents } from './useAnalyticsEvents'
import { useSalesforceEvents } from './useSalesforceEvents'
import { pesavelUND } from '../constants'
import { useBuyProduct } from './useBuyProduct'
import { useClubContext } from '../contexts/ClubContext'
import { useLocalStorageContext } from '../contexts/LocalStorageContext'
import { useRegionalizationContext } from '../contexts/RegionalizationContext'
import { useStoreContext } from '../contexts/StoreContext'
import { cartStore, useCart, type CartItem } from '../sdk/cart'
import { ProductsQueryResponse } from '../types/Product'
import { getWeightUnit } from '../utils/product/getWeightUnit'
import { validateQuantityBounds } from '../utils/product/validateQuantityBounds'

interface Props {
  product: ProductsQueryResponse['node']
  location: string
  clubPrice?: number
  setOpenClubModal: (value: boolean) => void
}

const min = 0

export const useProductCart = ({
  product,
  location,
  clubPrice,
  setOpenClubModal,
}: Props) => {
  const { skuSpecifications, unitMultiplier, measurementUnit, lowStock } =
    product
  const { removeItemEvent, addItemEvent } = useAnalyticsEvents()
  const { cartProductId, cartProductQty, buyProps } = useBuyProduct(product)
  const { isClubClient } = useClubContext()
  const { id: orderFormId } = useCart()
  const { removeItemEventSalesforce, addItemEventSalesforce } =
    useSalesforceEvents()
  const { updateItemQuantity, removeItem, getItem } = cartStore
  const { hasChangeOrder, hasClub } = useStoreContext()
  const {
    regionalized,
    setProduct,
    setOpenRegionModal,
    setLocationItem,
    setIsClubClient,
  } = useRegionalizationContext()
  const { getLocalStorageItem } = useLocalStorageContext()

  const [modalChangeOrder, setModalChangeOrder] = useState(false)
  const [quantity, setQuantity] = useState(cartProductQty)

  useEffect(() => {
    setQuantity(cartProductQty)
  }, [cartProductQty])

  const hasPesavelUnd = useMemo(
    () =>
      skuSpecifications?.some((specification) => {
        return (
          specification.field.originalName === pesavelUND &&
          specification.values.some((value) => value.originalName === 'true')
        )
      }),
    [skuSpecifications]
  )

  const user = useMemo(
    () => (isClubClient ? 'usuario logado' : 'usuario deslogado'),
    [isClubClient]
  )

  const productEvent = useMemo(
    () =>
      buyProps.itemOffered.hasClubPrice ? 'produto club' : 'produto normal',
    [buyProps.itemOffered.hasClubPrice]
  )

  const saveAddEventsOnOrderForm = useCallback(() => {
    axios.post(`/api/saveAddEventsOnOrderForm`, {
      orderFormId,
      id: buyProps.id,
      location,
    })
  }, [orderFormId, buyProps.id, location])

  const handleAddToCart = useCallback(() => {
    if (lowStock && Boolean(hasChangeOrder)) {
      setModalChangeOrder?.(true)
    }

    if (regionalized) {
      const modalView = getLocalStorageItem?.().clubModalViewed
      cartStore.addItem(buyProps)
      addItemEvent(buyProps, location, 'adicionar', productEvent, user)
      addItemEventSalesforce(buyProps)
      saveAddEventsOnOrderForm()

      setQuantity(1)

      if (clubPrice && hasClub && !modalView && !isClubClient) {
        setOpenClubModal(true)
      }

      return
    }

    setProduct?.(buyProps)
    setLocationItem?.(location)
    setIsClubClient?.(isClubClient ?? false)
    setOpenRegionModal?.(true)
  }, [
    lowStock,
    hasChangeOrder,
    regionalized,
    getLocalStorageItem,
    buyProps,
    location,
    productEvent,
    user,
    addItemEvent,
    addItemEventSalesforce,
    saveAddEventsOnOrderForm,
    clubPrice,
    hasClub,
    isClubClient,
    setOpenClubModal,
    setProduct,
    setLocationItem,
    setIsClubClient,
    setOpenRegionModal,
  ])

  const isPesavel = useMemo(
    () => measurementUnit !== 'un' && !hasPesavelUnd,
    [measurementUnit, hasPesavelUnd]
  )

  const NewMeasurementUnit = useMemo(
    () => getWeightUnit(measurementUnit ?? 'un', unitMultiplier, quantity),
    [measurementUnit, unitMultiplier, quantity]
  )

  const NewUnitMultiplier = useMemo(
    () => (isPesavel ? unitMultiplier : 1),
    [isPesavel, unitMultiplier]
  )

  const sku = useMemo(() => cartProductId.replace(/:.*$/, ''), [cartProductId])

  const item = getItem(sku) as CartItem

  const handleUpdateQuantity = useCallback(
    (quantityValue: number) => {
      if (quantityValue === 0) {
        removeItem(cartProductId)
        removeItemEventSalesforce(sku)
      } else {
        updateItemQuantity(cartProductId, quantityValue)
      }
    },
    [
      cartProductId,
      removeItem,
      removeItemEventSalesforce,
      sku,
      updateItemQuantity,
    ]
  )

  const changeQuantity = useCallback(
    (increaseValue: number) => {
      const quantityValue = validateQuantityBounds(
        quantity + increaseValue,
        min
      )

      handleUpdateQuantity?.(quantityValue)
      setQuantity(quantityValue)
    },
    [quantity, handleUpdateQuantity]
  )

  const increase = useCallback(() => {
    addItemEvent(item, location, 'aumentar', productEvent, user)
    changeQuantity(1)
  }, [
    quantity,
    addItemEvent,
    item,
    location,
    productEvent,
    user,
    changeQuantity,
  ])

  const decrease = useCallback(() => {
    removeItemEvent(sku, location, 'diminuir')
    changeQuantity(-1)
  }, [quantity, removeItemEvent, sku, location, changeQuantity])

  const quantityProp = useCallback(() => {
    if (NewUnitMultiplier * 1000 * quantity >= 1000) {
      return (NewUnitMultiplier * 1000 * quantity) / 1000
    }

    return NewUnitMultiplier * 1000 * quantity
  }, [NewUnitMultiplier, quantity])

  const hasUndQtd = useMemo(
    () => (hasPesavelUnd ? quantity : quantityProp()),
    [hasPesavelUnd, quantity, quantityProp]
  )

  const isLeftDisabled = useMemo(() => quantity === min, [quantity])

  const isRightDisabled = useMemo(() => quantity === 1000, [quantity])

  const quantitySelector = useMemo(
    () => ({
      sku,
      hasUndQtd,
      decrease,
      increase,
      measurementUnit: NewMeasurementUnit,
      unitMultiplier: NewUnitMultiplier,
      quantity,
      isLeftDisabled,
      isRightDisabled,
    }),
    [
      sku,
      hasUndQtd,
      decrease,
      increase,
      NewMeasurementUnit,
      NewUnitMultiplier,
      quantity,
      isLeftDisabled,
      isRightDisabled,
    ]
  )

  const productInCart = cartStore.inCart(cartProductId)

  return {
    quantitySelector,
    handleAddToCart,
    modalChangeOrder,
    setModalChangeOrder,
    orderFormId,
    productInCart,
  }
}
